import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/context/CartContext";
import { Navbar, scrollToId } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Journey } from "@/components/Journey";
import { Categories } from "@/components/Categories";
import { Catalogue } from "@/components/Catalogue";
import { CartDrawer } from "@/components/CartDrawer";
import { Trust } from "@/components/Trust";
import { ProductSlider } from "@/components/ProductSlider";
import { Footer } from "@/components/Footer";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { AdminPage } from "@/pages/AdminPage";
import { ProductsPage } from "@/pages/ProductsPage";
import { CATALOGUE_FALLBACK } from "@/data/catalogueFallback";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Home = ({ categories, onSelect }) => {
  const location = useLocation();

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09 });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    if (location.state?.scrollTo) {
      const target = location.state.scrollTo;
      setTimeout(() => scrollToId(target), 500);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  return (
    <div id="top" className="grain bg-[#FAF8FC] text-[#221533] min-h-screen">
      <Hero />
      <Journey />
      <Categories categories={categories} onSelect={onSelect} />
      <Trust />
      <ProductSlider categories={categories} />
      <Footer onSelect={onSelect} />
    </div>
  );
};

const Shell = () => {
  const [categories, setCategories] = useState(CATALOGUE_FALLBACK);
  const [activeCat, setActiveCat] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    axios
      .get(`${API}/catalogue`)
      .then((res) => setCategories(res.data.categories))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const selectCategory = (slug) => {
    setActiveCat(slug);
    if (location.pathname !== "/products") navigate("/products");
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 60);
  };

  return (
    <>
      {location.pathname !== "/admin" && <Navbar query={query} setQuery={setQuery} />}
      <Routes>
        <Route path="/" element={<Home categories={categories} onSelect={selectCategory} />} />
        <Route
          path="/products"
          element={
            <ProductsPage
              categories={categories}
              activeCat={activeCat}
              setActiveCat={setActiveCat}
              loading={loading}
              query={query}
              setQuery={setQuery}
              onSelect={selectCategory}
            />
          }
        />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
      <CartDrawer />
      <WhatsAppButton />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <CartProvider>
        <BrowserRouter>
          <Shell />
          <Toaster theme="light" position="bottom-left" />
        </BrowserRouter>
      </CartProvider>
    </div>
  );
}

export default App;
